// course.effect.ts

// effects/products.effects.ts
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  delay,
  map,
  mergeMap,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators'; // Import operators correctly
import { fromEvent, Observable, of } from 'rxjs';

import {
  ApplyTemplateToCourseAction,
  CreateCourseItemActions,
  CurrentCourseHeirarchyActions,
  CurriculumSeedProductActions,
  GenerateCourseAiHierarchyActions,
  GenerateLessonAiContentActions,
  MoveCourseItemActions,
  UpdateCourseSettingsActions,
  updateCurrentCourseHeirarchyStructure,
} from './course.actions';

import { Action, Store } from '@ngrx/store';
import {
  ChangeItemProcessStatus,
  CourseItemStatusActions,
  DeleteCourseItemActions,
} from './course-items/course-items.actions';

import { selectToken } from '../../user/auth/auth.selectors';
import { ApiService, AuthenticationService } from '../../../../services';
import { AiService } from '../../../../services/ai.service';
import {
  CourseHeirarchyModel,
  CourseHierarchyItemTypeEnum,
} from '../../../../../components/user-dashboard/product/models/course';

@Injectable()
export class CourseEffects {
  constructor(
    private actions$: Actions,
    private api: ApiService,
    private store: Store,
    private aiService: AiService,
    private authService: AuthenticationService,
  ) {}

  // Load Current course Heirarchy
  loadCurrentCourseHeirarchy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CurrentCourseHeirarchyActions.load), // Listen for the loadProducts action
      mergeMap(({ productId, errorHandler = undefined }) =>
        this.api.getCourseContentById(productId, errorHandler).pipe(
          // Use pipe method correctly within mergeMap
          map((response: CourseHeirarchyModel) =>
            CurrentCourseHeirarchyActions.loadSuccess({ heirarchy: response }),
          ),
          catchError((error) =>
            of(CurrentCourseHeirarchyActions.loadFailure({ error })),
          ),
        ),
      ),
    ),
  );

  // Create Course Item
  createCourseItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CreateCourseItemActions.create), // Listen for the loadProducts action
      mergeMap(({ productId, itemType, payload, errorHandler = undefined }) => {
        return this.api
          .genericAddCourseItem(productId, itemType, payload, errorHandler)
          .pipe(
            // Use pipe method correctly within mergeMap
            map((response: any) =>
              CreateCourseItemActions.success({ heirarchy: response }),
            ),
            catchError((error) =>
              of(CreateCourseItemActions.failure({ error })),
            ),
          );
      }),
    ),
  );

  // Move Course Item
  moveCurrentCourseItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MoveCourseItemActions.move), // Listen for the loadProducts action
      mergeMap(({ productId, payload, errorHandler = undefined }) =>
        this.api.moveCourseitem(productId, payload, errorHandler).pipe(
          // Use pipe method correctly within mergeMap
          map((response: CourseHeirarchyModel) =>
            MoveCourseItemActions.moveSuccess({ heirarchy: response }),
          ),
          catchError((error) =>
            of(MoveCourseItemActions.moveFailure({ error })),
          ),
        ),
      ),
    ),
  );

  generateCourseCurriculum$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CurriculumSeedProductActions.generate), // Listen for the loadProducts action
      tap((action) => console.log('Generate action received:', action)),
      mergeMap(({ courseId, courseData }) => {
        const prompt = `
        Generate a crash course outline on the topic "${courseData.name.replace('\n', '').replace('"', '')}".
        The course should include exactly ${courseData.aiState.curriculumAiData.moduleCount} modules${courseData.aiState.curriculumAiData.includeAssessment ? ' and 1 conclusion' : ''}.
        Each module must have ${courseData.aiState.curriculumAiData.lessonsPerModule} lesson titles.
        Each module title must begin with "Module {number}:" followed by the module name.
        ${courseData.aiState.curriculumAiData.includeAssessment ? 'The conclusion module must have at least 2 lesson titles.' : ''}

        - Provide only the titles of modules and lessons.
        - Do not include any extra text, introductions, explanations, or concluding remarks before or after the outline.
        - Format only should be exactly as shown below, the content should be a real content:
        `;
        return this.aiService
          .getChatCompletion([], prompt, {
            model: this.aiService.getCurrentModel(),
            stream: false,
          })
          .pipe(
            // Use pipe method correctly within mergeMap
            map((response: any) => {
              console.log('The Response:', response);
              return CurriculumSeedProductActions.generateSuccess({
                courseId,
                heirarchy: response,
              });
            }),
            catchError((error) => {
              console.log('The Error:', error);
              return of(
                CurriculumSeedProductActions.generateFailure({ error }),
              );
            }),
          );
      }),
    ),
  );

  generateCourseCurriculumSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CurriculumSeedProductActions.generateSuccess),
      tap((action) => console.log('Generate action received:', action)),
      map(({ courseId, heirarchy }) => {
        const parsedHeirarchy = parseCourseOutline(heirarchy);
        console.log('The Heirarchy:', parsedHeirarchy);
        return ApplyTemplateToCourseAction.set({
          productId: courseId,
          payload: { hierarchy: parsedHeirarchy },
        });
      }),
    ),
  );

  generateCourseCurriculumFromBackend$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GenerateCourseAiHierarchyActions.generate), // Listen for the loadProducts action
      mergeMap(({ courseId, courseData }) => {
        return this.api
          .generateCourseCurriculumFromBackend(courseId, courseData)
          .pipe(
            // Use pipe method correctly within mergeMap
            map((response: any) => {
              console.log(response);
              return GenerateCourseAiHierarchyActions.generateSuccess({
                courseId,
                heirarchy: response,
              });
            }),
            catchError((error) =>
              of(GenerateCourseAiHierarchyActions.generateFailure({ error })),
            ),
          );
      }),
    ),
  );

  // In course.effect.ts

  generateAllLessons$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(GenerateLessonAiContentActions.generateAll),
        withLatestFrom(this.store.select(selectToken)),
        switchMap(([action, initialToken]) => {
          const { courseId, justCheck = false } = action;

          return new Observable<Action>((observer) => {
            let eventSource: EventSource;
            let activeToken = initialToken || '';
            let isRetrying = false;

            const connect = (token: string) => {
              eventSource = this.api.generateAllLessonsContentFromBackend(
                courseId,
                token,
                justCheck,
              );

              eventSource.onmessage = (event) => {
                // ... same message handling ...
                const parsedData = JSON.parse(event.data);
                try {
                  const { type, total, lesson, completed } = parsedData.data;

                  if (
                    (type === 'progress' || type === 'lesson-done') &&
                    lesson?.id
                  ) {
                    observer.next(
                      GenerateLessonAiContentActions.generateUpdate({
                        courseId,
                        lessonId: lesson.id,
                        completed,
                        total,
                      }),
                    );

                    observer.next(
                      ChangeItemProcessStatus({
                        statusChange: {
                          itemId: lesson.id,
                          itemType: CourseHierarchyItemTypeEnum.lesson,
                          status:
                            type === 'lesson-done' ? 'done' : 'generating',
                        },
                      }),
                    );
                  }

                  if (type === 'done') {
                    observer.next(
                      GenerateLessonAiContentActions.generateSuccess({
                        courseId,
                        lessonId: null,
                      }),
                    );
                    eventSource.close();
                    observer.complete();
                  }
                } catch (err) {
                  // ... handle parse error ...
                }
              };

              eventSource.onerror = (err) => {
                console.error('SSE error', err);
                eventSource.close();

                if (!isRetrying) {
                  console.log(
                    'Attempting to refresh token and reconnect SSE...',
                  );
                  isRetrying = true;
                  this.authService.refreshTokenObservable().subscribe({
                    next: (newToken: string) => {
                      console.log('Token refreshed, reconnecting SSE...');
                      activeToken = newToken;
                      connect(newToken); // Reconnect with new token
                    },
                    error: (refreshErr: any) => {
                      console.error(
                        'Token refresh failed during SSE retry',
                        refreshErr,
                      );
                      observer.next(
                        GenerateLessonAiContentActions.generateFailure({
                          courseId,
                          error: err, // Return original error
                        }),
                      );
                      observer.complete();
                    },
                  });
                } else {
                  // Already retried once, fail.
                  observer.next(
                    GenerateLessonAiContentActions.generateFailure({
                      courseId,
                      error: err,
                    }),
                  );
                  observer.complete();
                }
              };
            };

            connect(activeToken);

            return () => {
              if (eventSource) eventSource.close();
            };
          });
        }),
      ),
  );

  // Apply Template to course heirarchy
  applyTemplateToCourseHeirarchy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApplyTemplateToCourseAction.set), // Listen for the loadProducts action
      mergeMap(({ productId, payload, errorHandler = undefined }) => {
        return this.api
          .applyTemplateToCourse(productId, payload, errorHandler)
          .pipe(
            // Use pipe method correctly within mergeMap
            map((response: any) =>
              ApplyTemplateToCourseAction.success({ heirarchy: response }),
            ),
            catchError((error) =>
              of(ApplyTemplateToCourseAction.failure({ error })),
            ),
          );
      }),
    ),
  );

  // Update Course Settings
  updateCourseSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateCourseSettingsActions.update), // Listen for the loadProducts action
      mergeMap(({ productId, payload, errorHandler = undefined }) => {
        return this.api
          .updateCourseSettingsById(productId, payload, errorHandler)
          .pipe(
            // Use pipe method correctly within mergeMap
            map((response: any) =>
              UpdateCourseSettingsActions.success({ heirarchy: response }),
            ),
            catchError((error) =>
              of(UpdateCourseSettingsActions.failure({ error })),
            ),
          );
      }),
    ),
  );

  // Generic Effects
  resetStatusAfterTimeout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CurrentCourseHeirarchyActions.loadSuccess,
        MoveCourseItemActions.moveSuccess,
        CreateCourseItemActions.success,
        ApplyTemplateToCourseAction.success,
        CourseItemStatusActions.success,
        DeleteCourseItemActions.success,
        UpdateCourseSettingsActions.success,
        GenerateCourseAiHierarchyActions.generateSuccess,
      ),
      delay(3000), // Adjust delay time as needed (in milliseconds)
      map(() => CurrentCourseHeirarchyActions.resetProcessingStatus()),
    ),
  );

  updateCurrentCourseHeirarchyStructureCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        MoveCourseItemActions.moveSuccess,
        CreateCourseItemActions.success,
        ApplyTemplateToCourseAction.success,
        CourseItemStatusActions.success,
        DeleteCourseItemActions.success,
        UpdateCourseSettingsActions.success,
        GenerateCourseAiHierarchyActions.generateSuccess,
      ), // Listen for the loadProducts action
      mergeMap(({ heirarchy }) => {
        return of(updateCurrentCourseHeirarchyStructure({ heirarchy }));
      }),
    ),
  );
}

function parseCourseOutline(response: string): any[] {
  const modules: any[] = [];
  const lines = response
    .split(/\r?\n/) // split into lines
    .map((l) => l.trim())
    .filter((l) => l.length > 0 && !l.toLowerCase().startsWith('the response'));

  let currentModule: any | null = null;

  for (const line of lines) {
    // Detect module line
    const moduleMatch = line.match(/^Module\s+(\d+):\s*(.+)$/i);
    if (moduleMatch) {
      if (currentModule) modules.push(currentModule); // push previous

      currentModule = {
        index: parseInt(moduleMatch[1], 10),
        name: moduleMatch[2].trim(),
        type: CourseHierarchyItemTypeEnum.module,
        items: [],
      };
      continue;
    }

    const conclusionMatch = line.match(/^Conclusion:?\s*(.+)?$/i);
    if (conclusionMatch) {
      if (currentModule) modules.push(currentModule);

      currentModule = {
        index: null,
        name: conclusionMatch[0]?.trim() || 'Conclusion',
        type: CourseHierarchyItemTypeEnum.module,
        items: [],
      };
      continue;
    }

    // Detect lesson with "Lesson X"
    const lessonMatch = line.match(/^Lesson\s+(\d+):\s*(.+)$/i);
    if (lessonMatch && currentModule) {
      currentModule.items.push({
        index: parseInt(lessonMatch[1], 10),
        name: lessonMatch[2].trim(),
        type: CourseHierarchyItemTypeEnum.lesson,
      });
      continue;
    }

    // Otherwise, treat as lesson without explicit numbering
    if (currentModule) {
      currentModule.items.push({
        index: currentModule.items.length + 1,
        name: line,
        type: CourseHierarchyItemTypeEnum.lesson,
      });
    }
  }

  if (currentModule) modules.push(currentModule);

  return modules;
}
